import React, { useState, useEffect } from 'react';
import ky from 'ky';
import { useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { Grid, TextField, Select, MenuItem, Typography } from '@material-ui/core';
import { List, ListItem, ListItemText, Divider, Checkbox } from '@material-ui/core';
import { Card, CardContent, CardActions, Button, IconButton, Tooltip } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import { GoodsStatusType, isGoodsStatusType, GoodsDb, GoodsDbHashType } from '../redux/shopSlice';
import { setSnackbar } from '../redux/workBackendSlice';
import useShopState from '../hooks/useShopState';
import useMakePublishData from '../hooks/useMakePublishData';

import { Hash, deepCopy, getCookie } from '../general';
import { formatVersion } from '.';
import { apiURL } from '../components/DigimagiProvider';
import HelpDialog, { HelpActionType } from './HelpDialog';

type EditPaletteGoodsDialogProps = {
  open: boolean;
  onClose: () => void;
  id?: string;
}
const EditPaletteGoodsDialog: React.FC<EditPaletteGoodsDialogProps> = ({ open, onClose, id }) => {
  const dispatch = useDispatch();
  const publishStatus: Hash<string> = { 'public': '公開', 'private': '限定公開', 'closed': '非公開' };
  const [goodsName, setGoodsName] = useState<string>();
  const [status, setStatus] = useState<GoodsStatusType>('closed');
  const [paletteName, setPaletteName] = useState<string>();
  const [initials, setInitials] = useState<string[]>([]);
  type BundledVersionType = 'latest' | number;
  const isBundledVersionType = (a: unknown): a is BundledVersionType => (a === 'latest' || typeof a === 'number')
  type GoodsDataType = GoodsDb & { id: string; version: BundledVersionType; }
  const [bundleGoods, setBundleGoods] = useState<GoodsDataType[]>([]);
  const [cardGoodsList, setCardGoodsList] = useState<GoodsDbHashType>({});
  const [showData, setShowData] = useState<Omit<GoodsDataType, 'version'>>();
  const [helpAction, setHelpAction] = useState<HelpActionType>({ open: false })
  const ownGoods = useShopState().ownGoods;
  const makePublishData = useMakePublishData();

  const exit = () => {
    setGoodsName(undefined);
    setStatus('closed');
    setPaletteName(undefined);
    setInitials([]);
    setBundleGoods([]);
    setShowData(undefined);
    onClose();
  }
  const selectStatus = (value: unknown) => {
    if (isGoodsStatusType(value)) {
      setStatus(value);
    }
  }
  const getChecked = (id: string) => {
    const ix = bundleGoods.findIndex((data => data.id === id));
    return ix >= 0;
  }
  const getCheckChanged = (id: string) => {
    const isInitial = Boolean(initials.find(data => data[0] === id));
    return isInitial !== getChecked(id);
  }
  const toggleBundled = (id: string, value: GoodsDb) => {
    const newList = deepCopy(bundleGoods);
    const ix = bundleGoods.findIndex((data => data.id === id));
    if (ix >= 0) newList.splice(ix, 1);
    else newList.push({ ...value, id, version: 'latest' });
    setBundleGoods(newList);
  }
  const changeBundledVersion = (id: string, value: unknown) => {
    const newList = deepCopy(bundleGoods);
    const ix = bundleGoods.findIndex((data => data.id === id));
    if (ix < 0 || !isBundledVersionType(value)) return;
    newList[ix].version = value;
    setBundleGoods(newList);
  }
  const toggleShowData = (id?: string) => {
    if (!id || (showData && id === showData.id)) setShowData(undefined);
    else {
      const data = cardGoodsList[id];
      setShowData({ id, ...data });
    }
  }
  const isEnough = () => {
    if (goodsName && goodsName !== ''
      && paletteName && paletteName !== ''
      && bundleGoods.length >= 1) return true;
    else return false;
  }
  const publishExe = async () => {
    if (!paletteName) return;
    const publishData = makePublishData('palette', id, { name: paletteName, toPublishIds: [] });
    if (!publishData) return;
    type PaletteDataType = { name: string; bundle_goods: [string, 'latest' | number][] }
    const paletteData: PaletteDataType = {
      name: paletteName,
      bundle_goods: bundleGoods.map(data => [data.id, data.version])
    };
    const palettes: Hash<PaletteDataType> = {};
    palettes[id || 'newpalette'] = paletteData;
    try {
      const sendData = { user_id: getCookie('user_id'), name: goodsName, kind: 'palette', hash: { ...publishData, palettes } };
      const query = await ky.post(`${apiURL}make_goods`, { json: sendData }).json() as any;
      console.log(query.data)
      dispatch(setSnackbar({ open: true, message: `${goodsName} を商品化しました` }));
      exit();
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    const newList: GoodsDbHashType = {};
    Object.keys(ownGoods).forEach(id => {
      const value = ownGoods[id];
      if (value.kind === 'card_template') newList[id] = value;
    })
    setCardGoodsList(newList);
  }, [ownGoods])

  return (
    <Dialog open={open} onClose={exit} maxWidth="md" fullWidth>
      <DialogTitle>
        { id ? 'パレット商品を編集します' : '新しいパレット商品を作成します' }
        <Tooltip title="パレット商品とは">
          <IconButton onClick={() => setHelpAction({ open: true, titleValue: 'パレット商品とは', contentValue: 'パレット商品とは、商品化したカードテンプレートをセットで販売する商品です。<br>含まれるカードテンプレート個別の公開状態は問わず、パレット商品が公開されていれば他ユーザはインストールすることができます。<br>パレット商品のアップデートでは、公開者はパレット名やバンドルしたカードテンプレートのバージョンや順序を更新することができますが、購入者がすでにインストールした商品をアップデートした場合はパレット名の変更とカードテンプレートの追加しか反映されません。' })}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2} style={{ marginBottom: 32 }}>
          <Grid item><TextField label="商品名" variant="outlined" value={goodsName} onChange={ev => setGoodsName(ev.currentTarget.value)} fullWidth /></Grid>
          <Grid item>
            <Select value={status} onChange={ev => selectStatus(ev.target.value)}>
              { Object.keys(publishStatus).map(key =>
                <MenuItem value={key}>{ publishStatus[key] }</MenuItem>
              ) }
            </Select>
          </Grid>
          <Grid item><TextField label="パレット名" variant="outlined" value={paletteName} onChange={ev => setPaletteName(ev.currentTarget.value)} fullWidth /></Grid>
        </Grid>
        <Typography variant="subtitle2">含まれるカードテンプレート</Typography>
        <List style={{ marginBottom: 32 }}>
          { bundleGoods.map((value, ix) => {
            return (
              <div key={ix}>
                <ListItem key={ix} button dense selected={value.id === showData?.id} onMouseDown={() => toggleShowData(value.id)}>
                  <ListItemText primary={value.name} />
                  <ListItemText secondary={
                    <Select value={value.version} onChange={ev => changeBundledVersion(value.id, ev.target.value)}
                      style={{ height: '20px' }}
                    >
                      <MenuItem value="latest">latest</MenuItem>
                      <MenuItem value={cardGoodsList[value.id].latest_version}>{ formatVersion(cardGoodsList[value.id].latest_version) }</MenuItem>
                    </Select>
                  } />
                </ListItem>
                { ix + 1 < bundleGoods.length && <Divider /> }
              </div>
            );
          }) }
        </List>
        <Typography variant="subtitle2">商品化したカードテンプレート</Typography>
        <Grid container>
          <Grid item xs={showData ? 6 : 11}>
            <List style={{ height: 54 * 6, overflow: 'auto' }}>
              { Object.keys(cardGoodsList).map((id, ix) => {
                const value = cardGoodsList[id];
                return (
                  <div key={ix}>
                    <ListItem key={ix} button selected={id === showData?.id} onClick={() => toggleShowData(id)} style={{ backgroundColor: getCheckChanged(id) ? id === showData?.id ? '#61b8ff' : '#61b8ff64' : undefined }}>
                      <Checkbox color="default" size="small" checked={getChecked(id)} onChange={() => toggleBundled(id, value)} />
                      <ListItemText primary={value.name} />
                      <ListItemText secondary={`v${formatVersion(value.latest_version)}`} />
                      <ListItemText secondary={value.deleted_at ? '削除済み' : publishStatus[value.status]} />
                    </ListItem>
                    { ix + 1 < Object.keys(cardGoodsList).length && <Divider /> }
                  </div>
                );
              }) }
            </List>
          </Grid>
          <Grid item xs={showData ? 6 : 1}>
            { showData &&
              <Card style={{ margin: 8, position: 'sticky', top: 108.5 }} elevation={3}>
                <CardContent>
                  <Grid container direction="column" spacing={1}>
                    <Grid item>
                      <Typography variant="caption">商品名</Typography>
                      <Typography variant="body1">{ showData.name }</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="caption">公開状態</Typography>
                      <Typography variant="body1">{ publishStatus[showData.status] }</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button color="secondary" onClick={() => setShowData(undefined)}>閉じる</Button>
                </CardActions>
              </Card>
            }
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="secondary" onClick={exit}>キャンセル</Button>
        <Button variant="text" color="primary" disabled={!isEnough()} onClick={publishExe}>
          { Boolean(id) ? '変更を保存して商品を更新する' : '作成する' }
        </Button>
      </DialogActions>
      <HelpDialog open={helpAction.open}
        onClose={() => setHelpAction({ open: false })}
        titleValue={helpAction.titleValue}
        contentValue={helpAction.contentValue}
      />
    </Dialog>
  );
}

export default EditPaletteGoodsDialog;
